<template>
    <div>
        <p class="has-text-white mb-6">
            Choose which e-mails and text messages you receive from us. Account messages always go to your e-mail so you never miss a login code or enrollment confirmation.
        </p>

        <ui-card class="mb-5" v-if="loaded">
            <div class="columns is-mobile is-vcentered">
                <div class="column is-narrow">
                    <i class="fad fa-message-sms fa-2x" :class="prefs.sms_opted_in ? 'has-text-primary' : 'has-text-grey-light'"></i>
                </div>
                <div class="column">
                    <div class="has-text-weight-bold is-size-5">Text Messages</div>
                    <p class="has-text-grey" v-if="prefs.sms_opted_in">Notification texts go to <strong>{{ prefs.phone }}</strong>. Reply STOP to any text to turn them off.</p>
                    <p class="has-text-grey" v-else-if="hasPhone">Get a text when a study plan, reflection, or application is reviewed.</p>
                    <p class="has-text-grey" v-else>Add a mobile phone number in <router-link :to="{name: 'account', params: {section: 'account'}}">Account Settings</router-link> to receive text messages.</p>
                </div>
                <div class="column is-narrow" v-if="prefs.sms_opted_in">
                    <b-switch :value="true" type="is-success" :disabled="saving" @input="setSms(false)">
                        <span class="is-size-7 has-text-weight-bold">On</span>
                    </b-switch>
                </div>
            </div>

            <div class="sms-consent" v-if="!prefs.sms_opted_in && hasPhone">
                <b-checkbox v-model="smsConsent" type="is-info">
                    <span class="is-size-7">
                        I agree to receive automated notification texts from my247ed.com at <strong>{{ prefs.phone }}</strong>.
                        Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help.
                        Consent is not a condition of purchase. See our <router-link to="/terms">Terms of Use</router-link> and <router-link to="/privacy">Privacy Policy</router-link>.
                    </span>
                </b-checkbox>
                <div class="has-text-right mt-3">
                    <b-button type="is-primary" size="is-small" :disabled="!smsConsent || saving" :loading="saving" @click="setSms(true)">
                        <i class="fas fa-check mr-2"></i>Turn On Text Messages
                    </b-button>
                </div>
            </div>
        </ui-card>

        <ui-card v-if="loaded" :key="renderKey">
            <div class="pref-row pref-head">
                <div class="pref-topic"></div>
                <div class="pref-channel">E-Mail</div>
                <div class="pref-channel">Text</div>
            </div>

            <div class="pref-row" v-for="topic in prefs.topics" :key="topic.key">
                <div class="pref-topic">
                    <div class="has-text-weight-bold">{{ topic.label }}</div>
                    <div class="is-size-7 has-text-grey">{{ topic.description }}</div>
                </div>
                <div class="pref-channel">
                    <span v-if="topic.required" class="is-size-7 has-text-grey">Always On</span>
                    <b-switch v-else :value="prefs.email_all && topic.email" type="is-success" size="is-small"
                              :disabled="saving || !prefs.email_all" @input="save([{topic: topic.key, channel: 'email', enabled: $event}])"/>
                </div>
                <div class="pref-channel">
                    <span v-if="topic.required" class="is-size-7 has-text-grey">Always On</span>
                    <span v-else-if="!topic.sms_available" class="has-text-grey-light">—</span>
                    <b-switch v-else :value="prefs.sms_opted_in && topic.sms" type="is-success" size="is-small"
                              :disabled="saving || !prefs.sms_opted_in" @input="save([{topic: topic.key, channel: 'sms', enabled: $event}])"/>
                </div>
            </div>

            <template #footer>
                <div class="columns is-mobile is-vcentered">
                    <div class="column is-size-7 has-text-grey">
                        <template v-if="prefs.email_all">Turn this off to stop every optional e-mail at once.</template>
                        <template v-else>You're unsubscribed from all optional e-mail. You'll still receive account messages.</template>
                    </div>
                    <div class="column is-narrow">
                        <b-switch :value="prefs.email_all" type="is-success" :disabled="saving" @input="save([{topic: 'all', channel: 'email', enabled: $event}])">
                            <span class="is-size-7 has-text-weight-bold">Optional E-Mail {{ prefs.email_all ? 'On' : 'Off' }}</span>
                        </b-switch>
                    </div>
                </div>
            </template>
        </ui-card>
    </div>
</template>

<script>
    import UiCard from "../UiCard";
    import {client as http} from "../../http_client";

    export default {
        name: "NotificationsSection",
        components: {UiCard},
        data() {
            return {
                prefs: {topics: []},
                loaded: false,
                saving: false,
                smsConsent: false,
                // Bumped on a failed save so switches re-sync to server state
                renderKey: 0
            };
        },
        computed: {
            hasPhone() {
                return String(this.prefs.phone || '').replace(/\D/g, '').length >= 10;
            }
        },
        methods: {
            load() {
                http.get('app/comm-prefs').then(res => {
                    this.prefs = res.data;
                    this.loaded = true;
                }).catch(() => {
                    this.$notify('error', 'Could Not Load', 'Your notification settings could not be loaded. Please try again.', {duration: 8000, permanent: false});
                });
            },
            save(changes) {
                this.saving = true;
                return http.put('app/comm-prefs', {changes}).then(res => {
                    this.prefs = res.data;
                    this.$notify('success', 'Settings Saved', 'Your notification settings have been updated.', {duration: 4000, permanent: false});
                }).catch(err => {
                    const message = (err.response && err.response.data && err.response.data.error) || 'Something went wrong saving your changes. Please try again.';
                    this.$notify('error', 'Could Not Save', message, {duration: 8000, permanent: false});
                    // Re-render switches from the server's state
                    this.renderKey++;
                    this.load();
                }).finally(() => {
                    this.saving = false;
                });
            },
            setSms(enabled) {
                this.save([{topic: 'all', channel: 'sms', enabled}]).then(() => {
                    this.smsConsent = false;
                });
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .sms-consent {
        border-top: 1px solid #eeeeee;
        margin-top: 1rem;
        padding-top: 1rem;
    }

    .pref-row {
        display: flex;
        align-items: center;
        padding: 0.85rem 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
            border-bottom: none;
        }

        .pref-topic {
            flex: 1;
            padding-right: 1rem;
        }

        .pref-channel {
            width: 5.5rem;
            text-align: center;
            flex-shrink: 0;

            @media (max-width: 768px) {
                width: 3.75rem;
            }
        }

        &.pref-head {
            padding-top: 0;
            font-size: 0.75rem;
            font-weight: bold;
            text-transform: uppercase;
            color: #888888;
        }
    }
</style>
